import { useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { subscribeJobAlerts } from "../../lib/api";

export const JobAlerts = ({ skills = [] }) => {
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleSkill = (skill) =>
    setSelected((s) => (s.includes(skill) ? s.filter((x) => x !== skill) : [...s, skill]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email to subscribe");
      return;
    }
    setBusy(true);
    try {
      await subscribeJobAlerts(email.trim(), selected);
      toast.success("You're subscribed to job alerts", {
        description: selected.length
          ? `We'll email you when new ${selected.join(", ")} roles go live.`
          : "We'll email you when any new role goes live.",
      });
      setEmail("");
      setSelected([]);
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-white/10 bg-ink-800/50 p-8 lg:p-10"
      data-testid="job-alerts-form"
    >
      <div className="flex items-start gap-4">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-sky-400/30 bg-sky-400/10 text-sky-400">
          <BellRing className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold tracking-tight text-white">Get job alerts</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-400">
            Be the first to know when new roles are posted. Pick the skills you care about — leave them all
            unselected to hear about every opening.
          </p>
        </div>
      </div>

      {skills.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="job-alerts-skills">
          {skills.map((skill) => {
            const active = selected.includes(skill);
            return (
              <button
                type="button"
                key={skill}
                onClick={() => toggleSkill(skill)}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                  active
                    ? "border-sky-400 bg-sky-400 text-ink-900"
                    : "border-white/15 text-slate-300 hover:border-sky-400/60 hover:text-white"
                }`}
                data-testid={`job-alerts-skill-${skill}`}
              >
                {skill}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full rounded-full border border-white/10 bg-white/[0.03] px-5 py-3 text-sm text-white outline-none focus:border-sky-400/70"
          data-testid="job-alerts-email"
        />
        <button
          type="submit"
          disabled={busy}
          className="shrink-0 rounded-full bg-sky-400 px-7 py-3 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300 disabled:opacity-60"
          data-testid="job-alerts-submit"
        >
          {busy ? "Subscribing…" : "Notify me"}
        </button>
      </div>
    </form>
  );
};

export default JobAlerts;
